import { useCallback, useState } from 'react';
import { useSupabase } from './useSupabase';
import type { CrewJoinRequest } from '../types/crew-join-request';

// ---------- utilitaire de normalisation ----------

function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // retire les accents
    .replace(/[^a-z0-9]/g, ''); // retire tout sauf alphanumériques
}

function isSimilarName(a: string, b: string): boolean {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  // même nom normalisé, ou l'un commence par l'autre (gère prénoms courts/diminutifs)
  return na === nb || na.startsWith(nb) || nb.startsWith(na);
}

// ---------- types ----------

export interface PotentialDuplicate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  city: string | null;
}

export interface DuplicateCheckResult {
  contacts: PotentialDuplicate[];
  /** true = même email → impossible de créer un second contact avec cet email */
  hasEmailConflict: boolean;
}

// ---------- hook ----------

export function useCrewJoinRequests() {
  const supabase = useSupabase();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<CrewJoinRequest[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crew_join_requests')
        .select('*, crew:crews(id, name, crew_number)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as CrewJoinRequest[]) || []);
    } catch (err) {
      console.error('[useCrewJoinRequests] fetchAll error:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const deleteRequest = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await supabase.from('crew_join_requests').delete().eq('id', id);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('[useCrewJoinRequests] deleteRequest error:', err);
        return false;
      }
    },
    [supabase]
  );

  /**
   * Détecte les contacts potentiellement en doublon avec la demande.
   * Priorité : même email → retourne immédiatement avec hasEmailConflict=true.
   * Sinon : même nom de famille (ilike) + prénom similaire (normalisé côté client).
   */
  const findPotentialDuplicates = useCallback(
    async (request: CrewJoinRequest): Promise<DuplicateCheckResult> => {
      // 1. Recherche par email (insensible à la casse via ilike)
      const { data: emailMatches } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, birth_date, city')
        .ilike('email', request.email);

      if (emailMatches && emailMatches.length > 0) {
        return { contacts: emailMatches as PotentialDuplicate[], hasEmailConflict: true };
      }

      // 2. Recherche par nom de famille (ilike pour case-insensitive)
      const { data: lastNameMatches } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, birth_date, city')
        .ilike('last_name', request.last_name);

      // Filtrage côté client : prénom similaire (insensible accents + casse)
      const similar = (lastNameMatches || []).filter((c: any) =>
        isSimilarName(c.first_name ?? '', request.first_name)
      );

      return { contacts: similar as PotentialDuplicate[], hasEmailConflict: false };
    },
    [supabase]
  );

  /**
   * Attache la demande à l'équipage, crée ou réutilise un contact, puis supprime la demande.
   * - existingContactId fourni  → met à jour ce contact et l'attache à l'équipage
   * - existingContactId absent  → crée un nouveau contact puis l'attache
   * Retourne le crew_id en cas de succès (pour redirection), null sinon.
   */
  const attachToCrewAndDelete = useCallback(
    async (request: CrewJoinRequest, existingContactId?: string): Promise<string | null> => {
      try {
        const contactPayload = {
          first_name: request.first_name,
          last_name: request.last_name,
          email: request.email,
          phone: request.phone,
          birth_date: request.birth_date,
          address: request.address,
          postal_code: request.postal_code,
          city: request.city,
          country: request.country,
          is_not_student: request.is_not_student,
          socio_professional_category: request.profession || null,
        };

        let contactId: string;

        // Résolution école : lookup par nom (insensible à la casse), création si absente
        let schoolId: string | null = null;
        if (!request.is_not_student && request.school?.trim()) {
          const schoolName = request.school.trim();
          const { data: existingSchool } = await supabase
            .from('schools')
            .select('id')
            .ilike('name', schoolName)
            .maybeSingle();

          if (existingSchool) {
            schoolId = existingSchool.id;
          } else {
            const { data: newSchool, error: schoolError } = await supabase
              .from('schools')
              .insert({ name: schoolName })
              .select('id')
              .single();
            if (!schoolError && newSchool) schoolId = newSchool.id;
          }
        }

        if (existingContactId) {
          // Réutiliser le contact existant en mettant à jour ses données
          contactId = existingContactId;
          await supabase
            .from('contacts')
            .update({ ...contactPayload, ...(schoolId ? { school_id: schoolId } : {}) })
            .eq('id', contactId);
        } else {
          const { data: newContact, error: contactError } = await supabase
            .from('contacts')
            .insert({ ...contactPayload, status: 'prospect', school_id: schoolId })
            .select('id')
            .single();

          if (contactError) throw contactError;
          contactId = newContact.id;
        }

        // Rôle : copilote si pilote déjà présent, pilote sinon
        const { data: existingMembers } = await supabase
          .from('crew_members')
          .select('role')
          .eq('crew_id', request.crew_id);

        const hasPilot = existingMembers?.some((m: any) => m.role === 'pilot');
        const role = hasPilot ? 'copilot' : 'pilot';

        const { error: memberError } = await supabase.from('crew_members').insert({
          crew_id: request.crew_id,
          contact_id: contactId,
          role,
        });

        if (memberError) throw memberError;

        await supabase.from('crew_join_requests').delete().eq('id', request.id);

        return request.crew_id;
      } catch (err) {
        console.error('[useCrewJoinRequests] attachToCrewAndDelete error:', err);
        return null;
      }
    },
    [supabase]
  );

  return {
    requests,
    loading,
    fetchAll,
    deleteRequest,
    findPotentialDuplicates,
    attachToCrewAndDelete,
  };
}
